<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'

const { t } = useI18n()
const authStore = useAuthStore()

interface Props {
  show: boolean
  selectedPaymentsCount: number
  defaultTemplate?: string
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  selectedPaymentsCount: 0,
  defaultTemplate: ''
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'send', data: { message: string, sendEmail: boolean }): void
}>()

// Message content
const messageContent = ref('')
const sendEmail = ref(false)
const charCount = computed(() => messageContent.value.length)

// Placeholders
const placeholders = [
  { label: t('paymentReminder.fullName'), value: '{__IMIE_I_NAZWISKO__}' },
  { label: t('paymentReminder.firstName'), value: '{__IMIE__}' },
  { label: t('paymentReminder.lastName'), value: '{__NAZWISKO__}' },
  { label: t('paymentReminder.amount'), value: '{__KWOTA__}' },
  { label: t('paymentReminder.paymentDate'), value: '{__DATA_PLATNOSCI__}' },
  { label: t('paymentReminder.paymentTitle'), value: '{__TYTUL_PLATNOSCI__}' },
  { label: t('paymentReminder.clubName'), value: '{__NAZWA_KLUBU__}' },
  { label: t('paymentReminder.paymentUrl'), value: '{__URL_DO_PLATNOSCI__}' }
]

// Preview content
const previewContent = computed(() => {
  if (!messageContent.value.trim()) {
    return ''
  }

  let content = messageContent.value

  // Replace placeholders with example values
  content = content.replace(/{__IMIE_I_NAZWISKO__}/g, `<strong>${t('paymentTemplate.exampleFullName')}</strong>`)
  content = content.replace(/{__IMIE__}/g, `<strong>${t('paymentTemplate.exampleFirstName')}</strong>`)
  content = content.replace(/{__NAZWISKO__}/g, `<strong>${t('paymentTemplate.exampleLastName')}</strong>`)
  content = content.replace(/{__KWOTA__}/g, `<strong>${t('paymentTemplate.exampleAmount')}</strong>`)
  content = content.replace(/{__DATA_PLATNOSCI__}/g, '<strong>2025-10-31</strong>')
  content = content.replace(/{__TYTUL_PLATNOSCI__}/g, `<strong>${t('paymentTemplate.examplePaymentTitle')}</strong>`)
  content = content.replace(/{__NAZWA_KLUBU__}/g, `<strong>${t('paymentTemplate.exampleClubName')}</strong>`)
  content = content.replace(/{__URL_DO_PLATNOSCI__}/g, '<strong>https://example.com/payment/abc123</strong>')

  // Convert newlines to <br>
  content = content.replace(/\n/g, '<br>')

  return content
})

// Default templates per language
const defaultTemplates: Record<string, string> = {
  pl: `Dzień dobry, przypominamy o konieczności uregulowania płatności.
Imię i nazwisko: {__IMIE_I_NAZWISKO__}
Tytuł płatności: {__TYTUL_PLATNOSCI__}
Wysokość płatności: {__KWOTA__}
Data płatności: {__DATA_PLATNOSCI__}

Link do płatności: {__URL_DO_PLATNOSCI__}

Pozdrawiamy,
{__NAZWA_KLUBU__}`,
  en: `Hello, this is a reminder about your payment.
Full name: {__IMIE_I_NAZWISKO__}
Payment title: {__TYTUL_PLATNOSCI__}
Payment amount: {__KWOTA__}
Payment date: {__DATA_PLATNOSCI__}

Payment link: {__URL_DO_PLATNOSCI__}

Best regards,
{__NAZWA_KLUBU__}`,
  de: `Guten Tag, wir erinnern Sie an Ihre ausstehende Zahlung.
Name: {__IMIE_I_NAZWISKO__}
Zahlungstitel: {__TYTUL_PLATNOSCI__}
Betrag: {__KWOTA__}
Zahlungsdatum: {__DATA_PLATNOSCI__}

Zahlungslink: {__URL_DO_PLATNOSCI__}

Mit freundlichen Grüßen,
{__NAZWA_KLUBU__}`,
  fr: `Bonjour, nous vous rappelons votre paiement en attente.
Nom complet : {__IMIE_I_NAZWISKO__}
Titre du paiement : {__TYTUL_PLATNOSCI__}
Montant : {__KWOTA__}
Date de paiement : {__DATA_PLATNOSCI__}

Lien de paiement : {__URL_DO_PLATNOSCI__}

Cordialement,
{__NAZWA_KLUBU__}`,
  es: `Hola, le recordamos su pago pendiente.
Nombre completo: {__IMIE_I_NAZWISKO__}
Título del pago: {__TYTUL_PLATNOSCI__}
Importe: {__KWOTA__}
Fecha de pago: {__DATA_PLATNOSCI__}

Enlace de pago: {__URL_DO_PLATNOSCI__}

Saludos cordiales,
{__NAZWA_KLUBU__}`,
  it: `Buongiorno, le ricordiamo il pagamento in sospeso.
Nome completo: {__IMIE_I_NAZWISKO__}
Titolo del pagamento: {__TYTUL_PLATNOSCI__}
Importo: {__KWOTA__}
Data di pagamento: {__DATA_PLATNOSCI__}

Link per il pagamento: {__URL_DO_PLATNOSCI__}

Cordiali saluti,
{__NAZWA_KLUBU__}`,
  pt: `Olá, lembramos do seu pagamento pendente.
Nome completo: {__IMIE_I_NAZWISKO__}
Título do pagamento: {__TYTUL_PLATNOSCI__}
Valor: {__KWOTA__}
Data de pagamento: {__DATA_PLATNOSCI__}

Link de pagamento: {__URL_DO_PLATNOSCI__}

Com os melhores cumprimentos,
{__NAZWA_KLUBU__}`,
  nl: `Goedendag, wij herinneren u aan uw openstaande betaling.
Volledige naam: {__IMIE_I_NAZWISKO__}
Betalingstitel: {__TYTUL_PLATNOSCI__}
Bedrag: {__KWOTA__}
Betaaldatum: {__DATA_PLATNOSCI__}

Betalingslink: {__URL_DO_PLATNOSCI__}

Met vriendelijke groet,
{__NAZWA_KLUBU__}`,
  cs: `Dobrý den, připomínáme Vám neuhrazenou platbu.
Jméno a příjmení: {__IMIE_I_NAZWISKO__}
Název platby: {__TYTUL_PLATNOSCI__}
Částka: {__KWOTA__}
Datum platby: {__DATA_PLATNOSCI__}

Odkaz na platbu: {__URL_DO_PLATNOSCI__}

S pozdravem,
{__NAZWA_KLUBU__}`,
  da: `Hej, vi minder dig om din udestående betaling.
Fulde navn: {__IMIE_I_NAZWISKO__}
Betalingstitel: {__TYTUL_PLATNOSCI__}
Beløb: {__KWOTA__}
Betalingsdato: {__DATA_PLATNOSCI__}

Betalingslink: {__URL_DO_PLATNOSCI__}

Med venlig hilsen,
{__NAZWA_KLUBU__}`,
  sv: `Hej, vi påminner dig om din utestående betalning.
Fullständigt namn: {__IMIE_I_NAZWISKO__}
Betalningstitel: {__TYTUL_PLATNOSCI__}
Belopp: {__KWOTA__}
Betalningsdatum: {__DATA_PLATNOSCI__}

Betalningslänk: {__URL_DO_PLATNOSCI__}

Med vänliga hälsningar,
{__NAZWA_KLUBU__}`,
  no: `Hei, vi minner deg om din utestående betaling.
Fullt navn: {__IMIE_I_NAZWISKO__}
Betalingstittel: {__TYTUL_PLATNOSCI__}
Beløp: {__KWOTA__}
Betalingsdato: {__DATA_PLATNOSCI__}

Betalingslenke: {__URL_DO_PLATNOSCI__}

Med vennlig hilsen,
{__NAZWA_KLUBU__}`,
  fi: `Hei, muistutamme sinua maksamattomasta maksustasi.
Koko nimi: {__IMIE_I_NAZWISKO__}
Maksun otsikko: {__TYTUL_PLATNOSCI__}
Summa: {__KWOTA__}
Maksupäivä: {__DATA_PLATNOSCI__}

Maksulinkki: {__URL_DO_PLATNOSCI__}

Ystävällisin terveisin,
{__NAZWA_KLUBU__}`,
  hu: `Tisztelt Tagunk, emlékeztetjük a függőben lévő fizetésre.
Teljes név: {__IMIE_I_NAZWISKO__}
Fizetés címe: {__TYTUL_PLATNOSCI__}
Összeg: {__KWOTA__}
Fizetési határidő: {__DATA_PLATNOSCI__}

Fizetési link: {__URL_DO_PLATNOSCI__}

Üdvözlettel,
{__NAZWA_KLUBU__}`,
  hr: `Poštovani, podsjećamo vas na nepodmireno plaćanje.
Puno ime: {__IMIE_I_NAZWISKO__}
Naziv plaćanja: {__TYTUL_PLATNOSCI__}
Iznos: {__KWOTA__}
Datum plaćanja: {__DATA_PLATNOSCI__}

Poveznica za plaćanje: {__URL_DO_PLATNOSCI__}

Srdačan pozdrav,
{__NAZWA_KLUBU__}`,
  lt: `Sveiki, primename apie neapmokėtą mokėjimą.
Vardas ir pavardė: {__IMIE_I_NAZWISKO__}
Mokėjimo pavadinimas: {__TYTUL_PLATNOSCI__}
Suma: {__KWOTA__}
Mokėjimo data: {__DATA_PLATNOSCI__}

Mokėjimo nuoroda: {__URL_DO_PLATNOSCI__}

Pagarbiai,
{__NAZWA_KLUBU__}`,
  ro: `Bună ziua, vă reamintim de plata restantă.
Nume complet: {__IMIE_I_NAZWISKO__}
Titlul plății: {__TYTUL_PLATNOSCI__}
Suma: {__KWOTA__}
Data plății: {__DATA_PLATNOSCI__}

Link de plată: {__URL_DO_PLATNOSCI__}

Cu stimă,
{__NAZWA_KLUBU__}`
}

// Initialize with default template or provided template
const initializeTemplate = () => {
  if (props.defaultTemplate) {
    messageContent.value = props.defaultTemplate
  } else {
    const locale = authStore.initData?.lang || 'pl'
    messageContent.value = defaultTemplates[locale] || defaultTemplates.en
  }
}

// Insert placeholder at cursor position
const insertPlaceholder = (placeholder: string) => {
  const textarea = document.getElementById('reminderMessageContent') as HTMLTextAreaElement
  if (!textarea) return

  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = messageContent.value

  // Insert placeholder at cursor position
  messageContent.value = text.substring(0, start) + placeholder + text.substring(end)

  // Move cursor after inserted placeholder
  setTimeout(() => {
    textarea.selectionStart = textarea.selectionEnd = start + placeholder.length
    textarea.focus()
  }, 0)
}

// Handle send
const handleSend = () => {
  if (!messageContent.value.trim()) {
    return
  }

  emit('send', {
    message: messageContent.value,
    sendEmail: sendEmail.value
  })
}

// Handle close
const handleClose = () => {
  emit('close')
}

// Watch for show prop changes to initialize template
import { watch } from 'vue'
watch(() => props.show, (newValue) => {
  if (newValue) {
    initializeTemplate()
  }
})
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="handleClose">
    <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
      <div class="sticky top-0 bg-white border-b-2 border-gray-300 p-4">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-bold text-gray-900">{{ t('paymentReminder.title') }}</h2>
          <button @click="handleClose" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
        <p class="text-sm text-gray-600 mt-1">
          {{ t('paymentReminder.selectedPaymentsCount', { count: selectedPaymentsCount }) }}
        </p>
      </div>

      <div class="p-4">
        <!-- Message Content -->
        <div class="mb-4">
          <label for="reminderMessageContent" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('paymentReminder.messageContent') }} <span class="text-red-600">*</span>
          </label>
          <textarea
            id="reminderMessageContent"
            v-model="messageContent"
            required
            rows="12"
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600 font-sans"
            :placeholder="t('paymentReminder.messagePlaceholder')"
          ></textarea>
          <p class="text-xs text-gray-500 mt-1">{{ t('paymentReminder.charCount') }}: <span>{{ charCount }}</span></p>
        </div>

        <!-- Placeholder Buttons -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('paymentReminder.insertVariable') }}:</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="placeholder in placeholders"
              :key="placeholder.value"
              type="button"
              @click="insertPlaceholder(placeholder.value)"
              class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
            >
              {{ placeholder.label }}
            </button>
          </div>
        </div>

        <!-- Preview -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('paymentReminder.preview') }}
          </label>
          <div class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-50 text-gray-700 min-h-[200px] whitespace-pre-wrap">
            <span v-if="!previewContent" class="text-gray-400 italic">{{ t('paymentReminder.previewPlaceholder') }}</span>
            <span v-else v-html="previewContent"></span>
          </div>
        </div>

        <!-- Send Email Checkbox -->
        <div class="mb-4">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              v-model="sendEmail"
              class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span class="text-sm font-medium text-gray-700">{{ t('paymentReminder.sendEmailToo') }}</span>
          </label>
          <p class="text-xs text-gray-500 mt-1 ml-6">{{ t('paymentReminder.sendEmailHint') }}</p>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <button
            @click="handleClose"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
          >
            {{ t('paymentReminder.cancel') }}
          </button>
          <button
            @click="handleSend"
            :disabled="!messageContent.trim()"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ t('paymentReminder.send') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
